import { DropDownOption, MrnIssuer } from 'src/interfaces/utility-interfaces';

import { AppConstants } from '../../constants/constants';
import { FormGroup } from '@angular/forms';
import { HttpService } from './http.service';
import { IOrganizationDisplayData } from '../../interfaces/utility-interfaces';
import { Injectable } from '@angular/core';
import { SiteServiceCodeService } from './site-service-code.service';
import { UtilService } from './util.service';
import { fhirR4 } from '@smile-cdr/fhirts';

export interface IOrganizationData {
  fhirId: string;
  number: string; // Facility Number, Site Number etc
  name: string;
  active: boolean;
  currentlyOffersCardiac: boolean;
  periodStart: Date;
  periodEnd: Date;
  pcrSystem: string;
}

export interface IOrganizationRelation {
  parentFhirId: string;
  parentNumber: string; // Facility Number, Site Number etc
  childFhirId: string;
  childNumber: string;
  active: boolean;
  periodStart: Date;
  periodEnd: Date;
}

@Injectable({
  providedIn: 'root'
})
export class FacilityService {

  allSiteDropDownOptions: DropDownOption[] = [];
  private allFacilitiesData: IOrganizationData[] = [];
  allSitesData: IOrganizationData[] = [];
  private allFacilitySiteRelations: IOrganizationRelation [] = [];
  private allActiveSiteDropdownOptions: DropDownOption[] = [];

  constructor(
    private utilService: UtilService,
    private httpService: HttpService,
    private siteService: SiteServiceCodeService) { 
    }

  buildOrganizationForDisplay(organization): IOrganizationDisplayData {
    const organizationForDisplay = {} as IOrganizationDisplayData;
    const resourceType = organization.resourceType;

    organizationForDisplay.id = organization.id;
    organizationForDisplay.name = organization.name;
    if (organization.address) {
      organizationForDisplay.address = this.utilService.getAddressFromResource(organization);
      if (resourceType === 'Location') {
        organizationForDisplay.street = organization.address.line[0];
        organizationForDisplay.city = organization.address.city;
        organizationForDisplay.province = organization.address.state;
        organizationForDisplay.postal = organization.address.postalCode;
      } else {
        organizationForDisplay.street = organization.address[0].line[0];
        organizationForDisplay.city = organization.address[0].city;
        organizationForDisplay.province = organization.address[0].state;
        organizationForDisplay.postal = organization.address[0].postalCode;
      }
    }

    const contactPoint = organization.contact;
    if (contactPoint && resourceType === 'Organization') {
      organizationForDisplay.email = this.utilService.getTelecom(contactPoint, 'email');
      organizationForDisplay.phone = this.utilService.getTelecom(contactPoint, 'phone');
    }

    return organizationForDisplay;
  }

  // This method builds either a FHIR Organization, or a FHIR Location
  // resource
  buildFHIRFacilityObject(facilityFormGroup: FormGroup, fhirResourceType, resourceId?) {
    let facility: any = {};
    if (fhirResourceType === AppConstants.organization) {
      facility = {} as fhirR4.Organization;
      facility.resourceType = AppConstants.organization;
    }
    if (fhirResourceType === AppConstants.location) {
      facility = {} as fhirR4.Location;
      facility.resourceType = AppConstants.location;
    }

    if (resourceId) {
      facility.id = resourceId;
    }

    if (facilityFormGroup.value.name) {
      facility.name = facilityFormGroup.value.name;
    }
    if (facilityFormGroup.value.phone || facilityFormGroup.value.email) {
      const contact = new fhirR4.OrganizationContact();
      contact.telecom = [];
      if (facilityFormGroup.value.phone) {
        const contactPoint = new fhirR4.ContactPoint();
        contactPoint.system = 'phone';
        contactPoint.value = facilityFormGroup.value.phone;
        contact.telecom.push(contactPoint);
      }
      if (facilityFormGroup.value.email) {
        const contactPoint = new fhirR4.ContactPoint();
        contactPoint.system = 'email';
        contactPoint.value = facilityFormGroup.value.email;
        contact.telecom.push(contactPoint);
      }
      facility.contact = [contact];
    }
    if (facilityFormGroup.value.street || facilityFormGroup.value.city || facilityFormGroup.value.province ||
      facilityFormGroup.value.postal) {
      const address = new fhirR4.Address();
      if (facilityFormGroup.value.street) {
        address.line = facilityFormGroup.value.street;
      }
      if (facilityFormGroup.value.city) {
        address.city = facilityFormGroup.value.city;
      }
      if (facilityFormGroup.value.province) {
        address.state = facilityFormGroup.value.province;
      }
      if (facilityFormGroup.value.postal) {
        address.postalCode = facilityFormGroup.value.postal;
      }
      address.country = 'Canada';
      facility.address = [address];
    }
    return facility;
  }

  getAllOrganizations() {
    const query = AppConstants.organization;
    return this.httpService.executeGETQueryWithParams(query, true);
  }

  // This method is used to populate drop down that related to display MRN or search MRN, only list active relations
  async getCardiacSitesForProfile(orgId: string): Promise<fhirR4.BundleEntry[]> {
    const sites: fhirR4.BundleEntry[] = [];
    const queryParams = `OrganizationAffiliation?role=facility-site&primary-organization=${orgId}` +
      `&_include=OrganizationAffiliation:participating-organization`;
    return new Promise<fhirR4.BundleEntry[]>((resolve, reject) => {
      this.httpService.executeGETQueryWithParams(queryParams, true).then(async (data: fhirR4.Bundle) => {
        if (data.entry) {
          const siteList = await this.siteService.getSitesPerformService();
          for (const entry of data.entry) {
            if (entry.resource.resourceType !== 'Organization') {
              // Ignore OrganizationAffiliation
              continue;
            }
            const site = entry.resource as fhirR4.Organization;
            const siteType = this.utilService.getCodeFromCodeableConceptStd(site.type, AppConstants.cardiacTypeSystem, 'code');
            if (siteType !== AppConstants.cardiacTypeValue || !site.extension?.find(x => x.url === AppConstants.mrnIssuerSystem) ||
              !site.active) {
              // Skip site which is has no PCR mrn defined or in-active site
              continue;
            }
            const siteNumber = +this.utilService.getIdentifierValueFromResource(entry.resource);
            if (siteList.some(s => s.siteNumber === siteNumber)) {
              sites.push(entry);
            }
          }
          resolve(sites);
        } else {
          resolve([]);
        }
      });
    });
  }

  // This method only check if site in Site to Service List or not, no matter site is expired or not
  async getSitesForProfile(orgId: string) {
    const sites: fhirR4.BundleEntry[] = [];
    const queryParams = `OrganizationAffiliation?role=facility-site&primary-organization=${orgId}` +
      `&_include=OrganizationAffiliation:participating-organization`;
    return new Promise<any>((resolve, reject) => {
      this.httpService.executeGETQueryWithParams(queryParams, true).then(async (data: fhirR4.Bundle) => {
        if (data.entry) {
          for (const entry of data.entry) {
            if (entry.resource.resourceType !== 'Organization') {
              // Ignore OrganizationAffiliation
              continue;
            }
            // add add sites - active or inative
            sites.push(entry);
          } 
          resolve(sites);
        } else {
          resolve([]);
        }
      });
    });
  }

  getServiceLocationsForProfile(orgId: string, activeOnly: boolean): Promise<fhirR4.BundleEntry[]> {
    const sites: fhirR4.BundleEntry[] = [];
    const queryParams = `OrganizationAffiliation?role=facility-site&primary-organization=${orgId}` +
      `&_include=OrganizationAffiliation:participating-organization`;
    return new Promise<any>((resolve, reject) => {
      this.httpService.executeGETQueryWithParams(queryParams, true).then(async (data: fhirR4.Bundle) => {
        if (data.entry) {
          for (const entry of data.entry) {
            if (entry.resource.resourceType !== 'Organization') {
              // Ignore OrganizationAffiliation
              continue;
            }
            if (activeOnly && !(entry.resource as fhirR4.Organization).active) {
              continue;
            }
            const siteNumber = this.utilService.getIdentifierValueFromResource(entry.resource);
            if (activeOnly) {
              // Check if the site is Cardiac site
              this.siteService.isSiteInActiveList(siteNumber).then((exist: boolean) => {
                if (exist) { 
                  sites.push(entry);
                }
              });
            } else {
              // Check if the site is Cardiac site
              this.siteService.isSiteInList(siteNumber).then((exist: boolean) => {
                if (exist) { 
                  sites.push(entry);
                }
              });
            }
          } 
          resolve(sites);
        } else {
          resolve([]);
        }
      });
    });
  }

  isSiteActive(siteFhirId: string): boolean {
    return this.allSitesData.find(x => x.fhirId === siteFhirId)?.active; 
  }

  getInactiveDropDownOptionById(siteFhirId: string): DropDownOption {
    const site = this.allSitesData.find(x => x.fhirId === siteFhirId);  
    const matched = this.allFacilitySiteRelations.filter(x => x.childFhirId === siteFhirId);
    if (matched?.length >= 1) {
      // Give a default value
      let facility = this.allSitesData.find (x => x.fhirId === matched[0].childFhirId);
      return this.getDisplayForDropDown(facility?.name, site);
    } else {
      return null;
    }
  }

  getSiteByIdCached(siteFhirId: string): IOrganizationData {
    if (!siteFhirId) {
      return null;
    }
    return this.allSitesData.find(x => x.fhirId === siteFhirId);  
  }

  getOrganizationNameById(orgId: string) {
    return new Promise<string>((resolve, reject) => {
      this.httpService.getResourceWithTypeAndId('Organization', orgId).then((organization: fhirR4.Organization) => {
        resolve(organization.name);
      });
    });
  }

  getOrganizationNameByIdCached(orgId: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (!this.allFacilitiesData || this.allFacilitiesData.length <= 0) { 
        this.httpService.getResourceWithTypeAndId('Organization', orgId).then((organization: fhirR4.Organization) => {
          resolve(organization.name);
        });
      } else {
        const facility = this.allFacilitiesData.find (x => x.fhirId === orgId); 
        resolve(facility?.name);
      }
    });
  }
  getOrganizationByReference(orgReference: string) {
    return this.httpService.executeGETQueryWithParams(orgReference, true);
  }

  getDisplayForDropDown(facilityName: string, site: IOrganizationData): DropDownOption {
    let facilityPrefix = '';
    if (facilityName && facilityName.toLocaleLowerCase() !== site.name.toLocaleLowerCase()) {
      facilityPrefix = `${facilityName} - `;
    }
    const option: DropDownOption = {
      display: `${facilityPrefix}${site.name} (${site.number})`,
      code: 'Organization/' + site.fhirId
    };
    return option;
  }

  getSiteDropDown(facilityName: string, site: fhirR4.Resource): DropDownOption {
    let returnObj: DropDownOption;

    const res = site as fhirR4.Organization;
    let displayName = res.name;
    if (facilityName?.toLocaleLowerCase() !== res.name.toLocaleLowerCase()) {
      displayName = `${facilityName} - ${displayName}`;
    }
    returnObj = {
        display: displayName + ' (' + this.utilService.getIdentifierValueFromResource(site) + ')',
        code: 'Organization/' + site.id
    };

    return returnObj;
  }

  getMrnSystemResource(resource: fhirR4.Resource) {
    const res = resource as fhirR4.Organization;
    const extension = res.extension?.find (x => x.url === AppConstants.mrnIssuerSystem);
    if (extension) {
      return extension.valueUri;
    }
    return null;
  }

  getAllOrganizationsByType(type: string): Promise<IOrganizationData[]> {
    return new Promise ((res) => {
      const facilities: IOrganizationData[] = [];
      const query = `Organization?_count=${AppConstants.QUERY_STRING_PARAM_COUNT_VALUE}&type=${type}`;
      this.httpService.executeGETQueryWithParams(query, true).then((data: fhirR4.Bundle) => {
        if (+data.entry.length > 0) {
          for (const entry of data.entry) {
            const org = entry.resource as fhirR4.Organization;
            const siteType = this.utilService.getCodeFromCodeableConceptStd(org.type, AppConstants.cardiacTypeSystem, 'code');
            const facility: IOrganizationData = {
              fhirId: org.id,
              number: this.utilService.getIdentifierValueFromResource(org),
              name: org.name,
              active: org.active,
              periodStart: null,
              periodEnd: null,
              pcrSystem: null,
              currentlyOffersCardiac: siteType === AppConstants.cardiacTypeValue
            };
            const expireExtension = org.extension.find(x => x.url === AppConstants.expirePeriodSystem);
            if (expireExtension) {
              facility.periodStart = expireExtension.valuePeriod?.start;
              facility.periodEnd = expireExtension.valuePeriod?.end;
            }
            const pcr = org.extension.find( x => x.url === AppConstants.mrnIssuerSystem)?.valueUri;
            facility.pcrSystem = pcr;
            facilities.push(facility);
          }
           
        }  
        res(facilities);
        
      });
    });
  } 
  getAllSiteDropDownOptions() : Promise<DropDownOption[]> { 
    return new Promise(async (res) => { 
      if (this.allActiveSiteDropdownOptions?.length > 0) {
        res (this.allActiveSiteDropdownOptions);
      }
      else {
        this.allSitesData = [];
        this.allSitesData =  await this.getAllOrganizationsByType('site');
        const query = `OrganizationAffiliation?role=facility-site&_include=OrganizationAffiliation:primary-organization&_include=OrganizationAffiliation:participating-organization&_count=${AppConstants.QUERY_STRING_PARAM_COUNT_VALUE}`;
        this.httpService.executeGETQueryWithParams(query, true).then((data: fhirR4.Bundle) => {
          if (+data.entry.length > 0) {
            const options: DropDownOption[] = [];
            this.allSiteDropDownOptions = [];
            this.allFacilitySiteRelations = []; 
            this.allFacilitiesData = [];
            for (const entry of data.entry) {
              if (entry.resource.resourceType === 'Organization') {
                const org = entry.resource as fhirR4.Organization;
                const siteType = this.utilService.getCodeFromCodeableConceptStd(org.type, AppConstants.cardiacTypeSystem, 'code');
                const facility: IOrganizationData = {
                  fhirId: org.id,
                  number: this.utilService.getIdentifierValueFromResource(org),
                  name: org.name,
                  active: org.active,
                  periodStart: null,
                  periodEnd: null,
                  pcrSystem: null,
                  currentlyOffersCardiac: siteType === AppConstants.cardiacTypeValue
                };
                const organizationType = this.utilService.getCodeFromCodeableConceptStd(org.type, AppConstants.organizationTypeCodeSystem, 'code');
                if (organizationType === 'facility') {
                  this.allFacilitiesData.push(facility);     // these are facilities 
                }
                const expireExtension = org.extension.find(x => x.url === AppConstants.expirePeriodSystem);
                if (expireExtension) {
                  facility.periodStart = expireExtension.valuePeriod?.start;
                  facility.periodEnd = expireExtension.valuePeriod?.end;
                }
                const pcr = org.extension.find( x => x.url === AppConstants.mrnIssuerSystem)?.valueUri;
                facility.pcrSystem = pcr;
              } else if (entry.resource.resourceType === 'OrganizationAffiliation') {
                const rel = entry.resource as fhirR4.OrganizationAffiliation;
                if(rel.organization) {
                  const relation: IOrganizationRelation = {
                    parentFhirId: this.utilService.getIdFromReference(rel.organization.reference),
                    parentNumber: rel.organization.identifier?.value,
                    childFhirId: this.utilService.getIdFromReference(rel.participatingOrganization.reference),
                    childNumber: rel.participatingOrganization.identifier?.value,
                    active: rel.active,
                    periodStart: rel.period?.start,
                    periodEnd: rel.period?.end
                  };
                  this.allFacilitySiteRelations.push(relation);
                } 
            }
          } 
          //get relationships
    
          this.allSitesData.forEach ((site) => {  
           // this.allFacilitySiteRelations = relations; 
            // facility is active, site is active, relation is active
            const rel = this.allFacilitySiteRelations.find(x => x.childFhirId === site.fhirId && site.active && x.active);
            if (rel) {
              const facility = this.allFacilitiesData.find (x => x.fhirId === rel?.parentFhirId && x.active); 
              options.push(this.getDisplayForDropDown(facility?.name, site));
            } else if (site.active) {
              options.push(this.getDisplayForDropDown('', site));
            }
            // Now get all options include inactive relationship
            const matched = this.allFacilitySiteRelations.filter(x => x.childFhirId === site.fhirId);
            if (matched?.length >= 1) {
              // Give a default value
              let facility = this.allSitesData.find (x => x.fhirId === matched[0].parentFhirId) ; 
              matched.forEach( relation => {
                const activeFacility = this.allSitesData.find (x => x.fhirId === relation?.parentFhirId && x.active); 
                if (activeFacility) {
                  facility = activeFacility;
                }
              });
              this.allSiteDropDownOptions.push(this.getDisplayForDropDown(facility?.name, site));
            } else {
              this.allSiteDropDownOptions.push(this.getDisplayForDropDown('', site));
            }
          });
          this.allActiveSiteDropdownOptions = options;
          res(options); 
          //end relationships
        }
        });
      } 
  });
  } 

  async getCardiacSiteMrnIssuers() { 
    const siteMrnIssuers: MrnIssuer[] = []; 
    if (! this.allSitesData || this.allSitesData.length <= 0) {
      this.allSitesData = await this.getAllOrganizationsByType('site');
    }
    const mrnSites = this.allSitesData.filter(x => x.pcrSystem);
    mrnSites?.forEach (site => {
      siteMrnIssuers.push( { CardiacSiteName: site.name, IssuerURI: site.pcrSystem, SiteNumber: site.number, SiteId: site.fhirId });
    });

    return siteMrnIssuers;
  }
}


